import React, { useMemo, useState } from "react";
import moment from "moment";
import { MAX_SESSIONS_MONTH, MIN_SESSIONS_MONTH } from "../../DeclarationCourse/SessionList";
import {
    initialCalendarMonth,
    mostCommonDuration,
    mostCommonStartTime,
    rowsFromSelectedDates,
    selectedDatesFromRows,
    toggleDate,
    toggleWeekday,
} from "../../DeclarationCourse/sessionCalendarHelpers";
import { MAX_COURSES_PER_MONTH } from "../../../Utils/monthlyQuota";
import { DV2 } from "../theme";
import { clock, dayLabel, hours, monthName, plural, studentFirstName, studentFullName } from "../format";
import { vocab } from "../vocabulary";
import { useDeclarationV2 } from "../DeclarationV2Context";
import WizardShell from "../layout/WizardShell";
import PlanningAside from "../layout/PlanningAside";
import StudentMonthPanel, { studentMonthFigures } from "../layout/StudentMonthPanel";
import { Badge, Button, Card, Notice, SectionTitle, TextLink, useDv2Theme } from "../layout/Ui";
import MonthCalendar from "../components/MonthCalendar";
import { DurationStepper, TimeSelect } from "../components/TimeFields";
import useMonthSessions from "../components/useMonthSessions";
import {
    dateConflict,
    describeExistingConflict,
    earliestStartOn,
    endsNextDay,
    enforceEarliestStart,
    firstSelectableDay,
    nextFreeStart,
    shiftRowsAfterConflicts,
    timeToIso,
    timeValue,
} from "../components/planningRules";

/** Prochain quart d'heure, pour proposer une heure de début par défaut. */
const nextQuarterHour = (): string => {
    const now = moment().startOf("minute");
    const remainder = now.minute() % 15;
    if (remainder !== 0) now.add(15 - remainder, "minute");
    return now.format("HH:mm");
};

/**
 * Étape 2, plusieurs cours (maquettes 1b et 2b) : calendrier du mois avec
 * raccourcis par jour de semaine, chevauchements signalés sur la date
 * cochée, heure et durée communes, puis « Générer les N séances ».
 */
const PlanningMultiStep = () => {
    const { teachr, state, update, goTo } = useDeclarationV2();
    const { fontFamily } = useDv2Theme();
    const student = state.student!;
    const child = student.child;
    const minDate = firstSelectableDay(student.activationDate);
    const firstMonth = minDate.slice(0, 7);
    const lastMonth = moment().add(1, "month").format("YYYY-MM");

    const [month, setMonth] = useState(() => initialCalendarMonth(state.sessions, minDate));
    const [selected, setSelected] = useState<string[]>(() => selectedDatesFromRows(state.sessions));
    const [initialDefaults] = useState(() => ({
        startTime: mostCommonStartTime(state.sessions),
        duration: mostCommonDuration(state.sessions),
    }));
    const [time, setTime] = useState<string | null>(() => timeValue(initialDefaults.startTime) ?? nextQuarterHour());
    const [duration, setDuration] = useState<number>(initialDefaults.duration);
    const [limitReached, setLimitReached] = useState(false);

    const { sessions: existing } = useMonthSessions(teachr?.id, month);
    const { sessions: clientSessions, loading: clientLoading } = useMonthSessions(teachr?.id, month, child?.clientId);

    const conflicts = useMemo(() => {
        const map = new Map<string, ReturnType<typeof dateConflict>>();
        selected.forEach((date) => {
            const conflict = dateConflict(date, time, duration, existing);
            if (conflict) map.set(date, conflict);
        });
        return map;
    }, [selected, time, duration, existing]);
    const conflictList = [...conflicts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    const nextDayEnd = endsNextDay(time, duration);

    const figures = studentMonthFigures(child, teachr);
    const monthKey = month.format("YYYY-MM");
    const isCurrentMonth = monthKey === moment().format("YYYY-MM");
    // La déclaration compte pour un seul cours du quota, quel que soit son nombre de séances (20 au plus).
    const quotaReached = isCurrentMonth && !!figures.quota?.reached;
    // Dates cochées sur un autre mois que celui affiché (le calendrier garde la sélection d'un mois à l'autre).
    const selectedElsewhere = selected.filter((d) => d.slice(0, 7) !== monthKey);
    const otherMonth = selectedElsewhere[0] ? monthName(moment(selectedElsewhere[0])) : null;
    // Jour de la validation du compte coché avec une heure trop tôt : l'heure sera décalée à la génération.
    const activationAdjustments = selected
        .map((date) => ({ date, earliest: earliestStartOn(date, student.activationDate) }))
        .filter((item): item is { date: string; earliest: string } => !!item.earliest && !!time && time < item.earliest);

    const apply = (result: { dates: string[]; refused: boolean }) => {
        setSelected(result.dates);
        setLimitReached(result.refused);
    };
    const canGenerate = selected.length >= MIN_SESSIONS_MONTH && !!time && !nextDayEnd;
    const generate = () => {
        if (!canGenerate) return;
        const prepared = enforceEarliestStart(
            rowsFromSelectedDates(
                selected,
                state.sessions,
                { startTime: timeToIso(time!, selected[0]), duration },
                { startTime: initialDefaults.startTime, duration: initialDefaults.duration }
            ),
            student.activationDate
        );
        // Une séance qui chevaucherait un cours existant garde sa date et commence après ce cours.
        const { rows, shifted } = shiftRowsAfterConflicts(prepared, existing);
        update({
            sessions: rows,
            shiftedSessions: shifted.map((s) => ({ key: s.key, date: s.date, to: s.to, studentName: s.after.studentName })),
            step: "sessions",
        });
        goTo("sessions");
    };
    const endLabel = time ? clock(moment(timeToIso(time)).add(duration, "hours")) : "";

    return (
        <WizardShell
            title={`Quand ont lieu les ${vocab.courses()} ?`}
            subtitle="Étape 2 sur 4"
            onBack={() => goTo("student")}
            asideMobile={<StudentMonthPanel child={child} teachr={teachr} compact />}
            aside={<PlanningAside current={2} month={month} clientSessions={clientSessions} clientSessionsLoading={clientLoading} />}
            footer={{
                onPrevious: () => goTo("student"),
                onNext: state.sessions.length >= MIN_SESSIONS_MONTH ? () => goTo("sessions") : undefined,
                nextDisabled: state.sessions.length < MIN_SESSIONS_MONTH,
                nextLabel: "Suivant",
            }}
        >
            <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: 14 }}>
                <Badge>{vocab.multipleSessionsTitle()}</Badge>
                <span style={{ color: DV2.muted }}>avec {studentFullName(child)}</span>
                <TextLink label="Changer" onClick={() => goTo("student")} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-5 items-start">
                <Card style={{ display: "flex", flexDirection: "column", gap: 16, padding: 22 }}>
                    <div style={{ fontSize: 14, color: DV2.muted, lineHeight: 1.5 }}>
                        Cochez les dates de vos {vocab.courses()}. Un clic sur un jour de la semaine coche tous ces jours du mois.
                    </div>
                    <MonthCalendar
                        month={month}
                        onMonthChange={setMonth}
                        firstMonth={firstMonth}
                        lastMonth={lastMonth}
                        selected={selected}
                        minDate={minDate}
                        onToggleDate={(date) => apply(toggleDate(selected, date, MAX_SESSIONS_MONTH))}
                        onToggleWeekday={(iso) => apply(toggleWeekday(selected, month, iso, MAX_SESSIONS_MONTH, minDate))}
                        conflicts={new Set(conflicts.keys())}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", fontSize: 13, color: DV2.muted }}>
                        <span>
                            <b style={{ color: DV2.text }}>{plural(selected.length, "date cochée", "dates cochées")}</b> sur {MAX_SESSIONS_MONTH} séances possibles pour {vocab.thisCourse()}{limitReached ? " : limite atteinte" : ""}
                            {selectedElsewhere.length > 0 && otherMonth && <>, dont {selectedElsewhere.length} en {otherMonth}</>}
                        </span>
                        <span>Cette déclaration comptera pour {vocab.aCourse()} sur les {MAX_COURSES_PER_MONTH} du mois</span>
                    </div>
                    {conflictList.length > 0 && (
                        <Notice tone="warning">
                            <b>{conflictList.length > 1 ? `${conflictList.length} dates en chevauchement.` : "Une date en chevauchement."}</b>
                            <ul style={{ margin: "6px 0", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 4 }}>
                                {conflictList.map(([date, conflict]) => {
                                    const free = time ? nextFreeStart(date, time, duration, existing) : null;
                                    return (
                                        <li key={date}>
                                            <b>{dayLabel(date)}</b> : {describeExistingConflict(conflict!)}{" "}
                                            {free ? `La séance commencera à ${free.time.replace(":", "h")}.` : "Aucun créneau libre ce jour-là après ce début."}{" "}
                                            <TextLink label="Décocher" size={12} onClick={() => apply(toggleDate(selected, date, MAX_SESSIONS_MONTH))} />
                                        </li>
                                    );
                                })}
                            </ul>
                            Vous pouvez garder {conflictList.length > 1 ? "ces dates" : "cette date"} : à la génération, {conflictList.length > 1 ? "ces séances sont placées" : "cette séance est placée"} juste après le {vocab.course()} existant, et vous pourrez encore {conflictList.length > 1 ? "les" : "l'"}ajuster.
                        </Notice>
                    )}
                    {nextDayEnd && (
                        <Notice tone="danger">
                            Avec ce début et cette durée, les séances se termineraient le lendemain à {nextDayEnd.replace(":", "h")}. Réduisez la durée ou avancez l'heure de début.
                        </Notice>
                    )}
                    {quotaReached && (
                        <Notice tone="danger">
                            Quota de {MAX_COURSES_PER_MONTH} {vocab.courses()} atteint pour {studentFirstName(child)} en {monthName(moment())}. Choisissez des dates du mois suivant.
                        </Notice>
                    )}
                    {activationAdjustments.length > 0 && (
                        <Notice tone="info">
                            Le compte de {studentFirstName(child)} a été validé le {dayLabel(student.activationDate!).toLowerCase()} à {clock(student.activationDate!)} : la séance de ce jour commencera à {clock(timeToIso(activationAdjustments[0].earliest))}.
                        </Notice>
                    )}
                </Card>

                <div className="flex flex-col gap-3">
                    <Card style={{ display: "flex", flexDirection: "column", gap: 16, padding: 22 }}>
                        <SectionTitle>Même horaire et même durée pour toutes les séances</SectionTitle>
                        <div style={{ fontSize: 13, color: DV2.muted, lineHeight: 1.5, marginTop: -8 }}>
                            Vous pourrez ajuster l'heure et la durée de chaque séance après la génération.
                        </div>
                        <TimeSelect id="dv2-multi-time" value={time} onChange={setTime} chips />
                        <DurationStepper id="dv2-multi-duration" value={duration} onChange={setDuration} />
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, borderTop: `1px solid ${DV2.borderSoft}`, paddingTop: 12 }}>
                            <span style={{ color: DV2.muted }}>Fin de séance</span>
                            <b>{endLabel || "—".replace("—", "")}</b>
                        </div>
                    </Card>
                    <Button
                        label={`Générer ${selected.length >= 2 ? `les ${selected.length} séances` : "les séances"}`}
                        onClick={generate}
                        disabled={!canGenerate}
                        fullWidth
                    />
                    <div style={{ fontSize: 13, color: DV2.muted, textAlign: "center", lineHeight: 1.45, fontFamily }}>
                        {selected.length < MIN_SESSIONS_MONTH
                            ? `Cochez au moins ${MIN_SESSIONS_MONTH} dates. Pour une seule date, déclarez plutôt ${vocab.aCourse()} seul.`
                            : `Vous pourrez ensuite ajuster chaque séance une par une (${hours(duration * selected.length)} au total).`}
                    </div>
                </div>
            </div>
        </WizardShell>
    );
};

export default PlanningMultiStep;
